import { useState, useEffect, lazy, Suspense, useMemo, memo } from "react";
import { motion } from "framer-motion";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import LoadingScreen from "./components/loadingScreen";
import TargetCursor from "./components/TargetCursor";

// Lazy load all page components for code splitting
const NewPortfolio = lazy(() => import("./pages/NewPortfolio"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const CoinPulse = lazy(() => import("./pages/CoinPulse"));
const Simulare = lazy(() => import("./pages/Simulare"));
const BioTrack = lazy(() => import("./pages/BioTrack"));
const XPStrength = lazy(() => import("./pages/XPStrength"));
const NCLChampionship = lazy(() => import("./pages/NCLChampionship"));
const PicoCTF = lazy(() => import("./pages/PicoCTF"));
const PicoCTF2026 = lazy(() => import("./pages/PicoCTF2026"));
const MaritimeDefense = lazy(() => import("./pages/MaritimeDefense"));
const Reelize = lazy(() => import("./pages/Reelize"));

// Memoized page wrapper to prevent unnecessary re-renders
const PageWrapper = memo(function PageWrapper({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {children}
    </motion.div>
  );
});

// Route color mapping - defined outside component to prevent recreation
const ROUTE_COLORS = {
  '/simulare': 'red',
  '/biotrack': 'green',
  '/xpstrength': 'purple',
  '/ncl-championship': 'gold',
  '/picoctf-2025': 'gold',
  '/picoctf-2026': 'gold',
  '/maritime-defense': 'blue',
  '/reelize': 'blue',
};

const AppContent = memo(function AppContent() {
  const location = useLocation();

  // Memoize cursor color based on pathname
  const cursorColor = useMemo(() => {
    return ROUTE_COLORS[location.pathname] || 'blue';
  }, [location.pathname]);

  return (
    <>
      <TargetCursor
        spinDuration={5}
        hideDefaultCursor={true}
        color={cursorColor}
      />

      <PageWrapper>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Main single-page portfolio */}
            <Route path="/" element={<NewPortfolio />} />

            {/* Individual project pages */}
            <Route path="/coinpulse" element={<CoinPulse />} />
            <Route path="/simulare" element={<Simulare />} />
            <Route path="/biotrack" element={<BioTrack />} />
            <Route path="/xpstrength" element={<XPStrength />} />

            {/* Competition pages */}
            <Route path="/ncl-championship" element={<NCLChampionship />} />
            <Route path="/picoctf-2025" element={<PicoCTF />} />
            <Route path="/picoctf-2026" element={<PicoCTF2026 />} />
            <Route path="/maritime-defense" element={<MaritimeDefense />} />
            <Route path="/reelize" element={<Reelize />} />

            {/* Keep project detail page for other projects */}
            <Route path="/projects/:projectId" element={<ProjectDetail />} />
          </Routes>
        </Suspense>
      </PageWrapper>
    </>
  );
});

export default function App() {
  // Skip loading screen during react-snap prerendering
  const isPrerendering = navigator.userAgent === 'ReactSnap';
  const [loading, setLoading] = useState(!isPrerendering);

  useEffect(() => {
    if (isPrerendering) return;
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [isPrerendering]);

  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen overflow-x-hidden">
          {loading ? <LoadingScreen /> : <AppContent />}
        </div>
      </Router>
    </HelmetProvider>
  );
}
